import React, { useEffect } from 'react';
import { ChevronDown, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSite } from '@/context/SiteContext';
import MusicPlayer from '@/components/music/MusicPlayer';
import VideoGallery from '@/components/video/VideoGallery';
import ContactForm from '@/components/contact/ContactForm';

const HomePage = () => {
  const { content, loading } = useSite();

  // Scroll to top on page load/refresh
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* ============ HERO SECTION ============ */}
      <section 
        id="home" 
        className="relative min-h-screen flex items-end justify-center overflow-hidden pb-44 sm:pb-20 lg:pb-20 pt-16 bg-background"
      >
        {/* Background Image - contained with black sides */}
        <div className="absolute inset-x-0 top-16 bottom-0 flex items-center justify-center">
          <div className="relative w-full h-full max-w-5xl mx-auto">
            {content.hero_image ? (
              <img 
                src={content.hero_image}
                alt={content.hero_title}
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 15%' }}
              />
            ) : (
              <div className="w-full h-full bg-muted" />
            )}
          </div>
          {/* Black side bars */}
          <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-background via-background to-transparent" />
          <div className="absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-background via-background to-transparent" />
          {/* Bottom gradient for text only */}
          <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-background via-background/70 to-transparent" />
        </div>

        {/* Content - positioned at bottom */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl mb-6 slide-up tracking-tight whitespace-nowrap" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.7)' }}>
            {content.hero_title}
          </h1>
          <p className="text-lg sm:text-xl text-foreground/70 max-w-2xl mx-auto mb-10 fade-in" style={{ animationDelay: '0.3s', textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}>
            {content.hero_tagline}
          </p>
          
          <div className="flex items-center justify-center gap-4 fade-in" style={{ animationDelay: '0.5s' }}>
            <Button 
              onClick={() => scrollToSection('#music')}
              className="h-12 px-8 rounded-full bg-foreground text-background hover:bg-foreground/90 transition-all hover:scale-105"
            >
              <Play className="h-4 w-4 mr-2" />
              Listen Now
            </Button>
            <Button 
              variant="outline"
              onClick={() => scrollToSection('#about')}
              className="h-12 px-8 rounded-full border-foreground/30 hover:bg-foreground/10"
            >
              Learn More
            </Button>
          </div>
        </div>

        {/* Scroll indicator */}
        <button 
          onClick={() => scrollToSection('#about')}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground hover:text-foreground transition-colors animate-bounce"
        >
          <ChevronDown className="h-8 w-8" />
        </button>
      </section>

      {/* ============ ABOUT SECTION ============ */}
      <section id="about" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-muted shadow-2xl">
                {content.about_image ? (
                  <img 
                    src={content.about_image}
                    alt={content.hero_title}
                    className="w-full h-full object-cover"
                  />
                ) : content.hero_image ? (
                  <img 
                    src={content.hero_image}
                    alt={content.hero_title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-muted-foreground">Photo</span>
                  </div>
                )}
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-border rounded-2xl -z-10" />
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">
                {content.about_title}
              </p>
              <h2 className="font-serif text-4xl lg:text-5xl mb-8 leading-tight">
                The Story Behind<br />the Music
              </h2>
              <div className="prose prose-invert max-w-none">
                {content.about_text.split(/\n\n+/).map((paragraph, idx) => (
                  <p key={idx} className="text-muted-foreground leading-relaxed mb-4">
                    {paragraph.replace(/\n/g, ' ')}
                  </p>
                ))}
              </div>
              
              <Button 
                onClick={() => scrollToSection('#contact')}
                className="mt-8 h-12 px-8 rounded-full bg-foreground text-background hover:bg-foreground/90"
              >
                Get In Touch
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MUSIC SECTION ============ */}
      <section id="music" className="py-24 lg:py-32 bg-card/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Discography
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl mb-4">
              Music
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Explore the collection of compositions, film scores, and musical works.
            </p>
          </div>

          <MusicPlayer />
        </div>
      </section>

      {/* ============ VIDEOS SECTION ============ */}
      <section id="videos" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Watch
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl mb-4">
              Videos
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Performances, music videos, and behind-the-scenes content.
            </p>
          </div>

          <VideoGallery />
        </div>
      </section>

      {/* ============ CONTACT SECTION ============ */}
      <section id="contact" className="py-24 lg:py-32 bg-card/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Contact
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl mb-4">
              {content.contact_title}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {content.contact_text}
            </p>
          </div>

          <ContactForm />
        </div>
      </section>
    </div>
  );
};

export default HomePage;
